import React, { useState } from "react";
import PropTypes from "prop-types";
import { fetchData, getOrCreatePlayerForUser } from "../utils/api.js";
import { Drawer } from "./common/Drawer.js";
import { getUserLocation } from "../utils/mapkit.js";
import { Button } from "./ui/Button";

const CreateTeamModal = ({ isOpen, onClose, onTeamCreated, user, league }) => {
  const [formData, setFormData] = useState({
    team_name: "",
    team_city: "",
    team_state: "",
  });
  const [isPlayer, setIsPlayer] = useState(false);
  const [submitStatus, setSubmitStatus] = useState("");
  const [isLocating, setIsLocating] = useState(false);

  const handleUseLocation = async () => {
    try {
      setIsLocating(true);
      const loc = await getUserLocation();
      if (loc?.results?.length > 0) {
        const result = loc.results[0];
        if (result.formattedAddress) {
          const parts = result.formattedAddress
            .split("\n")
            .map((p) => p.trim())
            .filter(Boolean);
          const cityStateLine =
            parts.length > 1 ? parts[parts.length - 1] : parts[0];
          const match = cityStateLine?.match(/^([^,]+),\s*([A-Z]{2}|\w+)/i);
          if (match) {
            setFormData((prev) => ({
              ...prev,
              team_city: match[1],
              team_state: match[2].slice(0, 2).toUpperCase(),
            }));
            return;
          }
        }
      }
    } catch (err) {
      console.warn("Failed location lookup:", err);
    } finally {
      setIsLocating(false);
    }
  };

  if (!isOpen) return null;

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const getOrCreateCoachPlayerId = async (currentUser) => {
    return await getOrCreatePlayerForUser(currentUser);
  };

  const resolveOrCreateSeason = async (leagueId) => {
    if (!leagueId) return null;
    try {
      const seasons = await fetchData(`/seasons?league_id=eq.${leagueId}`);
      if (seasons && seasons.length > 0) {
        return seasons[0].season_id || seasons[0].id;
      }
      const newSeasonRes = await fetchData(`/seasons`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify({
          league_id: leagueId,
          name: "Regular Season",
        }),
      });
      if (newSeasonRes && newSeasonRes.length > 0) {
        return newSeasonRes[0].season_id || newSeasonRes[0].id;
      }
    } catch (seasonErr) {
      console.warn("Could not find or create season for team:", seasonErr);
    }
    return null;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus("submitting");

    try {
      const leagueId = league?.league_id || league?.id;
      const seasonId = await resolveOrCreateSeason(leagueId);

      const teamPayload = {
        team_name: formData.team_name,
        city: formData.team_city,
        state: formData.team_state,
      };
      if (seasonId) {
        teamPayload.season_id = seasonId;
      }

      const result = await fetchData(`/teams`, {
        method: "POST",
        headers: { Prefer: "return=representation" },
        body: JSON.stringify(teamPayload),
      });

      if (result && result.length > 0) {
        const newTeam = result[0];
        const coachPlayerId = await getOrCreateCoachPlayerId(user);

        if (coachPlayerId) {
          await fetchData(`/team_rosters`, {
            method: "POST",
            body: JSON.stringify({
              team_id: newTeam.team_id,
              person_id: coachPlayerId,
              role: "coach",
            }),
          });
        }

        setFormData({
          team_name: "",
          team_city: "",
          team_state: "",
        });
        setIsPlayer(false);
        setSubmitStatus("");
        onTeamCreated(newTeam);
      } else {
        setSubmitStatus("Error creating team.");
      }
    } catch (error) {
      console.error("Failed to create team:", error);
      setSubmitStatus(`Error: ${error.message}`);
    }
  };

  return (
    <Drawer isOpen={true} onClose={onClose} title="Create New Team">
      <form onSubmit={handleSubmit} className="form-compact">
        <div className="form-group full-width">
          <label htmlFor="team_name">Team Name</label>
          <input
            type="text"
            id="team_name"
            name="team_name"
            value={formData.team_name}
            onChange={handleInputChange}
            required
            style={{ boxSizing: "border-box", width: "100%" }}
          />
        </div>

        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            marginTop: "0.75rem",
            marginBottom: "0.5rem",
          }}
        >
          <span
            style={{
              fontSize: "0.9rem",
              fontWeight: 600,
              color: "var(--color-text-secondary)",
            }}
          >
            Team Location
          </span>
          <Button
            variant="primary"
            type="button"

            onClick={handleUseLocation}
            disabled={isLocating}
          >
            <span>{isLocating ? "Locating City..." : "Use My Location"}</span>
          </Button>
        </div>
        {isLocating && (
          <div
            className="location-status-badge"
            style={{ marginBottom: "8px" }}
          >
            <span
              className="location-heartbeat"
              style={{ fontSize: "12px", display: "inline-block" }}
            >
              ●
            </span>{" "}
            Location services active... Parsing coordinates & city...
          </div>
        )}

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="team_city">City</label>
            <input
              type="text"
              id="team_city"
              name="team_city"
              value={formData.team_city}
              onChange={handleInputChange}
              required
              style={{ boxSizing: "border-box", width: "100%" }}
            />
          </div>
          <div className="form-group">
            <label htmlFor="team_state">State</label>
            <input
              type="text"
              id="team_state"
              name="team_state"
              value={formData.team_state}
              onChange={handleInputChange}
              required
              maxLength={2}
              style={{ boxSizing: "border-box", width: "100%" }}
            />
          </div>
        </div>

        <div className="form-group full-width" style={{ marginTop: "1.25rem" }}>
          <div
            tabIndex={0}
            onClick={() => setIsPlayer(!isPlayer)}
            onKeyDown={(e) => {
              if (e.key === "Enter" || e.key === " ") {
                e.preventDefault();
                setIsPlayer(!isPlayer);
              }
            }}
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              padding: "12px 16px",
              borderRadius: "10px",
              background: "var(--color-surface)",
              border: isPlayer
                ? "1.5px solid var(--color-turquoise)"
                : "1px solid var(--color-border)",
              cursor: "pointer",
              userSelect: "none",
            }}
            role="switch"
            aria-checked={isPlayer}
          >
            <span
              style={{
                fontWeight: "600",
                color: "var(--color-text-primary)",
              }}
            >
              I am also a player on this team
            </span>
            <div
              style={{
                width: "38px",
                height: "22px",
                borderRadius: "11px",
                background: isPlayer
                  ? "var(--color-turquoise)"
                  : "var(--color-border)",
                position: "relative",
                transition: "background 0.25s ease",
                flexShrink: 0,
                marginLeft: "12px",
              }}
            >
              <div
                style={{
                  width: "18px",
                  height: "18px",
                  borderRadius: "50%",
                  background: "#ffffff",
                  position: "absolute",
                  top: "2px",
                  left: isPlayer ? "18px" : "2px",
                  transition: "left 0.2s cubic-bezier(0.34, 1.56, 0.64, 1)",
                  boxShadow: "0 1px 3px rgba(0,0,0,0.25)",
                }}
              />
            </div>
          </div>
        </div>

        <div className="modal-actions" style={{ marginTop: "1rem" }}>
          <Button variant="outline" type="button" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            type="submit"

            disabled={submitStatus === "submitting"}
          >
            {submitStatus === "submitting" ? "Creating..." : "Create Team"}
          </Button>
        </div>
        {submitStatus?.includes("Error") && (
          <p className="error-text" style={{ marginTop: "1rem" }}>
            {submitStatus}
          </p>
        )}
      </form>
    </Drawer>
  );
};

CreateTeamModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTeamCreated: PropTypes.func.isRequired,
  user: PropTypes.object.isRequired,
  league: PropTypes.object,
};

export default CreateTeamModal;
